import { useEffect, useMemo, useState } from "react";
import { aiTaakVoorstel, aiWizardVoorstel, useAchtergrondTaak } from "./achtergrondAi";
import { useList, useTranslate } from "@refinedev/core";
import { SelectField } from "@fawkes/ui";

import { sectionsForContext, tileComponent } from "./tiles";
import type { ContextType, SettingsContext, SettingsDetailKey } from "./tiles/types";
import { AiKoppelingDetail } from "./AiKoppelingDetail";
import { PlatformConnectionDetail } from "./PlatformConnectionDetail";
import { FinanceSettings } from "./FinanceSettings";
import { PensionSettings } from "./PensionSettings";
import { PleeggezinnenSettings } from "./PleeggezinnenSettings";
import { DierenStamgegevens } from "./DierenStamgegevens";
import { BreedWeightRanges } from "./BreedWeightRanges";
import { VoedingSettings } from "./VoedingSettings";
import { TakenRulesSettings } from "./TakenRulesSettings";
import { WizardBuilderSettings, type WizardBuilderInit } from "./WizardBuilderSettings";
import "./SettingsDashboard.css";

// jve-settings: the React port of the Jmix `SettingsDashboardView` (route `instellingen`). This is
// the orchestrator: the context combobox (Organisatie vs. a specific vestiging), the context badge,
// and the per-context section grid. Each configuration group is its own component (see ./tiles/*),
// composed here via the registry — so per-tile migration sessions own one file each.
//
// The combobox drives the whole view: the user picks "Organisatie" (organisation-wide settings) or
// one of the vestigingen they have access to. `/api/vestigingen` already returns only the
// authenticated user's accessible branches (VestigingAccessService), so the option list is correctly
// scoped. The selection is local to this page — it does not mutate the global current vestiging.

// Shape returned by GET /api/vestigingen (see VestigingController / VestigingResponse, matching the
// header's VestigingSwitcher).
interface Vestiging {
  id: number;
  naam?: string | null;
  displayName?: string | null;
  plaats?: string | null;
}

interface ContextOption {
  id: string;
  type: ContextType;
  label: string;
  vestigingId: number | null;
}

function vestigingLabel(v: Vestiging): string {
  const name = v.displayName?.trim() || v.naam?.trim();
  return name ? `Vestiging ${name}` : `Vestiging #${v.id}`;
}

export function SettingsDashboard({
  wizardBuilderInit = null,
}: {
  /**
   * nazorg-authoring: a deep-open request from NazorgBeheer — land directly on the wizard-builder
   * detail (open a definition / create a nazorg-vragenlijst). Consumed once: leaving the builder
   * ("Terug") clears it, so re-opening the Wizards tile behaves normally.
   */
  wizardBuilderInit?: WizardBuilderInit | null;
} = {}) {
  const t = useTranslate();
  const { data: vestigingenData } = useList<Vestiging>({ resource: "vestigingen" });

  const contextOptions = useMemo<ContextOption[]>(() => {
    const options: ContextOption[] = [
      { id: "org", type: "organization", label: t("instellingen.context.organization"), vestigingId: null },
    ];
    for (const v of vestigingenData?.data ?? []) {
      options.push({ id: `branch-${v.id}`, type: "branch", label: vestigingLabel(v), vestigingId: v.id });
    }
    return options;
  }, [vestigingenData, t]);

  const [selectedContextId, setSelectedContextId] = useState("org");
  // A migrated tile detail screen is hosted in-place (state nav, like the rest of the SPA shell) with
  // a "Terug" back button — mirroring the Jmix dashboard's detail dialog. A wizardBuilderInit deep-open
  // (NazorgBeheer → builder) lands straight on the wizards detail.
  const [activeDetail, setActiveDetail] = useState<SettingsDetailKey | null>(() => {
    if (wizardBuilderInit) return "wizards";
    const taak = aiTaakVoorstel.get();
    if (taak.status === "klaar" && taak.bekijken) return "taken";
    const wizard = aiWizardVoorstel.get();
    return wizard.status === "klaar" && wizard.bekijken ? "wizards" : null;
  });
  // "Bekijken" in de melding van een AI-voorstel terwijl je al in Instellingen staat: naar dat scherm.
  // Dat scherm pakt het resultaat zelf op (het taakconcept in de editor, de nieuwe wizard geopend).
  const aiTaak = useAchtergrondTaak(aiTaakVoorstel);
  const aiWizard = useAchtergrondTaak(aiWizardVoorstel);
  useEffect(() => {
    if (aiTaak.status === "klaar" && aiTaak.bekijken) setActiveDetail("taken");
  }, [aiTaak]);
  useEffect(() => {
    if (aiWizard.status === "klaar" && aiWizard.bekijken) setActiveDetail("wizards");
  }, [aiWizard]);
  const [builderInit, setBuilderInit] = useState<WizardBuilderInit | null>(wizardBuilderInit);

  const selected = contextOptions.find((o) => o.id === selectedContextId) ?? contextOptions[0];
  const context: SettingsContext = {
    type: selected?.type ?? "organization",
    label: selected?.label ?? t("instellingen.context.organization"),
    vestigingId: selected?.vestigingId ?? null,
  };

  if (activeDetail === "ai-koppeling") {
    return (
      <div className="settings-view-root">
        <div className="settings-view-content">
          <AiKoppelingDetail onBack={() => setActiveDetail(null)} />
        </div>
      </div>
    );
  }

  if (activeDetail === "platform-koppeling") {
    return (
      <div className="settings-view-root">
        <div className="settings-view-content">
          <PlatformConnectionDetail onBack={() => setActiveDetail(null)} />
        </div>
      </div>
    );
  }

  if (activeDetail === "finance") {
    return (
      <div className="settings-view-root">
        <div className="settings-view-content">
          <FinanceSettings context={context} onBack={() => setActiveDetail(null)} />
        </div>
      </div>
    );
  }

  if (activeDetail === "pension") {
    return (
      <div className="settings-view-root">
        <div className="settings-view-content">
          <PensionSettings context={context} onBack={() => setActiveDetail(null)} />
        </div>
      </div>
    );
  }

  if (activeDetail === "pleeggezinnen") {
    return (
      <div className="settings-view-root">
        <div className="settings-view-content">
          <PleeggezinnenSettings context={context} onBack={() => setActiveDetail(null)} />
        </div>
      </div>
    );
  }

  if (activeDetail === "animals") {
    return (
      <div className="settings-view-root">
        <div className="settings-view-content">
          <DierenStamgegevens onBack={() => setActiveDetail(null)} />
        </div>
      </div>
    );
  }

  if (activeDetail === "voeding") {
    return (
      <div className="settings-view-root">
        <div className="settings-view-content">
          <VoedingSettings context={context} onBack={() => setActiveDetail(null)} />
        </div>
      </div>
    );
  }

  if (activeDetail === "breed-weights") {
    return (
      <div className="settings-view-root">
        <div className="settings-view-content">
          <BreedWeightRanges context={context} onBack={() => setActiveDetail(null)} />
        </div>
      </div>
    );
  }

  if (activeDetail === "taken") {
    return (
      <div className="settings-view-root">
        <div className="settings-view-content">
          <TakenRulesSettings context={context} onBack={() => setActiveDetail(null)} />
        </div>
      </div>
    );
  }

  if (activeDetail === "wizards") {
    return (
      <div className="settings-view-root">
        <div className="settings-view-content">
          <WizardBuilderSettings
            context={context}
            init={builderInit ?? undefined}
            onBack={() => {
              // Leaving the builder consumes the deep-open request — the Wizards tile then opens
              // the plain list again instead of re-running the create/open.
              setBuilderInit(null);
              setActiveDetail(null);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-view-root">
      <div className="settings-view-content">
        <div className="settings-context-row">
          <span className="settings-context-label">{t("instellingen.contextLabel")}</span>
          <SelectField
            label=""
            className="settings-context-selector"
            aria-label={t("instellingen.contextLabel")}
            value={selectedContextId}
            onChange={(e) => setSelectedContextId(e.target.value)}
            options={contextOptions.map((o) => ({ value: o.id, label: o.label }))}
          />
        </div>

        <div className="settings-context-banner-row">
          <span className="settings-context-badge">
            {t("instellingen.activeContext", { args: [context.label] })}
          </span>
        </div>

        <div className="settings-tile-grid">
          {sectionsForContext(context.type).map((section) => (
            <div key={section.titleKey} className="settings-tile-section">
              <div className="settings-tile-section-grid">
                {section.tileKeys.map((key) => {
                  const Tile = tileComponent(key);
                  return Tile ? <Tile key={key} context={context} onOpenDetail={setActiveDetail} /> : null;
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
